import * as React from 'react';
import Card from '../../../../layout/card/Card';
import { css } from '@emotion/core';
import CardHeader from '../../../../layout/card/CardHeader';
import { FLEX_ADAPTIVE } from '../../../../layout/flexBox';
import { COLORS } from '../../../../globalStyle/colors';
import { Str } from '../../../../localization/translationKeys';
import { onMobileDevice } from '../../../../globalStyle/mediaQueries';
import { Routes } from '../../../../navigation/routes';
import { ParticipantProject, Requirement } from '../../../models';
import RequirementSummary from '../../../requirements/RequirementSummary';
import { useMsgText } from '../../../../localization/hooks';
import RaterCounts from './RaterCounts';
import RaterSummaryChart from './RaterSummaryChart';
import { isNilOrEmpty } from '../../../../language/module';
import { withValidMinOrMax, withValidMinimum } from '../module';


const styles = {
  root:         css`
  `,
  content:      css`
    margin-top: 20px;
    justify-content: space-between;
  `,
  raterCounts:  css`
    height: 180px;
    padding-top: 15px;
    
    &[dir="ltr"] {
      padding-left: 4%;
    }
    
    &[dir="rtl"] {
      padding-right: 4%;
    }
    
    ${onMobileDevice} { 
      margin-bottom: 40px;
      padding: 0 !important;
    }
  `,
  completed:    css`
    flex-grow: 1;
    flex-shrink: 0;
    margin: 0 20px;
    
    ${onMobileDevice} { 
      margin-bottom: 70px;
    }
  `,
  requirements: css`
    padding: 5px 30px;
    
    &[dir="ltr"] {
      border-left: 1px solid ${COLORS.LIGHT_GREY};
    }
    
    &[dir="rtl"] {
      border-right: 1px solid ${COLORS.LIGHT_GREY};
    }
    
    ${onMobileDevice} {
      border: none !important;
      padding: 5px;
    }
  `,
};


type Props = {
  routes: Routes;
  proj: ParticipantProject | undefined;
  requirements: Requirement[] | undefined;
  className?: string;
};


/**
 * RaterStatusCard: a component that renders the card on the dashboard that shows the status of the project's raters
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterStatusCard: React.FC<Props> = props => {
  const cardTitle = useMsgText(Str.RaterStatus);

  const nonEmptyRequirements = React.useMemo(
    () => !!props.proj?.maximumsRequired ? withValidMinOrMax(props.requirements) : withValidMinimum(props.requirements),
    [props.proj, props.requirements],
  );

  if (!props.proj) {
    return <div className={props.className} />;
  }

  const inviteRemindDisabled = props.proj?.surveyPastDue
      || (props.proj?.followupSurveyStep && !props.proj?.followupSurveyCreated)
      || !props.proj?.inviteRatersStep
      || !props.proj?.courseAllowInviteRaters;
  return (
    <Card css={styles.root} className={props.className}>
      <CardHeader title={cardTitle} />
      <div css={[FLEX_ADAPTIVE, styles.content]}>
        {/* Invite/Remind Raters w/ Counts */}
        <RaterCounts
          routes={props.routes}
          requirements={props.requirements}
          surveyPastDue={props.proj.surveyPastDue}
          inviteRatersEnabled={!inviteRemindDisabled}
          css={styles.raterCounts}
        />

        {/* Completed Surveys Chart */}
        <RaterSummaryChart requirements={props.requirements} css={styles.completed} />

        {/* Recommended/Required Rater Minimums */}
        {
          !isNilOrEmpty(props.requirements) && (
            <RequirementSummary
              requirements={nonEmptyRequirements}
              minimumsRequired={props.proj.minimumsRequired}
              css={styles.requirements}
            />
          )
        }
      </div>
    </Card>
  );
};

export default RaterStatusCard;

